/**
 * A-GTCC-VIEW-ALIN1 — "Alerts & Incidents" (alin), 1:1 after
 * `docs/design/GTCC_REFERENCE_2026-09-01.html:585-605`.
 *
 * P17 of the Owner programme: the alarm WORK lives here and only here. The
 * cockpit shows counters and jumps into this view; this view holds the two
 * operator verbs — acknowledge and assign an owner — and nothing that touches
 * infrastructure.
 *
 * Element for element of the reference:
 *   - head title "Alerts & Incidents" + freshness badge, `.sub` line,
 *   - panel "Aktive Alarme" with the mono/muted `h2` addition and the six
 *     column table dot | ALARM (Owner-Text) | SYSTEM | SEIT | BESITZER |
 *     AKTION,
 *   - `grid-main` with "Incident-Arbeitsraum" and "Stumm-Schaltungen
 *     (begrenzt)".
 *
 * Two deliberate deviations from the reference, both required by the
 * instruction rather than invented here:
 *   - the `.sub` line drops the trailing "Beispieldaten." — this view reads a
 *     real endpoint,
 *   - `[Ack]` and `[Besitzer]` are real buttons, and `[Ack]` opens a small
 *     dialog for reason and duration, because an acknowledging silence is
 *     never created without a reason and never longer than
 *     {@link ACK_MAX_HOURS} hours. The reference draws the pair as static
 *     mono text; it cannot draw a dialog.
 *
 * Nothing here computes an age from the local clock: `ageSeconds` arrives
 * decided by the API, so a stale source can never look recent (P8/P9/P10).
 */
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  Modal,
  Stack,
  TextInput,
} from "@genius-tracker/design-system";
import type { Locale } from "@genius-tracker/i18n";
import { useLocale } from "@genius-tracker/i18n/react";
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { useCallback, useState, type CSSProperties, type ReactNode } from "react";

import {
  ACK_MAX_HOURS,
  ALERTS_SUMMARY_KEY,
  ackGtccAlert,
  fetchGtccAlertsSummary,
  setGtccAlertOwner,
  type AlertSeverity,
  type AlertsResult,
  type GtccAlertRowDto,
  type GtccAlertsSummaryDto,
} from "../../api/alerts.js";
import {
  GTCC_VISUALLY_HIDDEN,
  PANEL_HEADING_STYLE,
  formatInteger,
  type FleetTone,
} from "../cockpit/components/FleetPrimitives.js";
import {
  CELL_RULE_STYLE,
  MONO_CELL_STYLE,
  ROW_STYLE,
  TABLE_STYLE,
  TD_STYLE,
  TH_STYLE,
} from "../control/ControlPrimitives.js";
import { Dot, FreshBadge, type CockpitTone } from "../../ui/primitives.js";

/* --------------------------------------------------------------- queries */

/** Same floor as the cockpit: never refetch faster than 30 s. */
const ALIN_STALE_MS = 30_000;
const ALIN_REFETCH_MS = 60_000;

const ALIN_QUERY_OPTIONS = {
  staleTime: ALIN_STALE_MS,
  refetchInterval: ALIN_REFETCH_MS,
  refetchOnWindowFocus: false,
} as const;

/* ---------------------------------------------------------------- tones */

/** Severity -> dot tone; the reference paints crit, warn and info dots. */
const SEVERITY_TONE: Readonly<Record<AlertSeverity, FleetTone>> = {
  critical: "crit",
  warning: "warn",
  info: "info",
};

/* ------------------------------------------------------------ formatting */

const MINUTE_SECONDS = 60;
const HOUR_SECONDS = 3_600;
const DAY_SECONDS = 86_400;

/**
 * `SEIT` of the reference: "41 min", "2 h". The unit is chosen by the size of
 * the number the API sent — nothing is rounded up into a friendlier unit.
 */
function formatAge(
  seconds: number,
  locale: Locale,
  t: (key: string, params?: Record<string, string | number>) => string,
): string {
  if (seconds < MINUTE_SECONDS) {
    return t("admin.gtcc.alin.age.seconds", { seconds: formatInteger(seconds, locale) });
  }
  if (seconds < HOUR_SECONDS) {
    return t("admin.gtcc.alin.age.minutes", {
      minutes: formatInteger(Math.floor(seconds / MINUTE_SECONDS), locale),
    });
  }
  if (seconds < DAY_SECONDS) {
    return t("admin.gtcc.alin.age.hours", {
      hours: formatInteger(Math.floor(seconds / HOUR_SECONDS), locale),
    });
  }
  return t("admin.gtcc.alin.age.days", {
    days: formatInteger(Math.floor(seconds / DAY_SECONDS), locale),
  });
}

/* ------------------------------------------------------- call-site pieces */

/** The em dash the reference uses where a cell is simply empty. */
function FleetDash(props: { testId?: string }): ReactNode {
  return (
    <span data-testid={props.testId} style={{ color: "var(--gt-sys-text-muted)" }}>
      &mdash;
    </span>
  );
}

/** A muted note under the rows of a panel — the reference sets it in mono. */
function FleetNote(props: { children: ReactNode; testId?: string }): ReactNode {
  return (
    <p
      data-testid={props.testId}
      style={{
        margin: 0,
        fontFamily: "var(--gt-sys-font-family-mono)",
        fontSize: "var(--gt-cmp-gtcc-mono-size)",
        color: "var(--gt-sys-text-muted)",
      }}
    >
      {props.children}
    </p>
  );
}

/**
 * `Dot` (`ui/primitives.tsx`) carries no `testId`/`data-tone` props of its
 * own; the wrapper below is the call-site adaptation that keeps those two
 * attributes (asserted by `AlinView.test.tsx`) without bending the shared
 * primitive. Same recipe as `LaunchView.tsx`'s `LaunchDot`.
 */
function AlinDot(props: { tone: CockpitTone; testId: string }): ReactNode {
  return (
    <span data-testid={props.testId} data-tone={props.tone}>
      <Dot tone={props.tone} />
    </span>
  );
}

/**
 * `FreshBadge` derives the age from the real measurement and carries no
 * `testId`/`data-stale` of its own, so this wrapper retains those test hooks.
 */
function AlinFreshBadge(props: {
  measuredAt: string | null;
  stale?: boolean;
  testId: string;
}): ReactNode {
  const stale = props.stale === true;
  return (
    <span data-testid={props.testId} data-stale={stale ? "true" : "false"}>
      <FreshBadge measuredAt={props.measuredAt} stale={stale} />
    </span>
  );
}

/* ----------------------------------------------------------------- styles */

const HEAD_ROW_STYLE: CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: "var(--gt-sys-space-3)",
  flexWrap: "wrap",
};

/*
 * Head style of the reference `h1`. The element itself is an `h2`: inside the
 * cockpit the `h1` belongs to `GtccShell` (`gtcc-shell-heading`), and a second
 * one would give the page two document titles. Every GTCC view therefore opens
 * at level 2 (see `views/risk/GtccRiskView.tsx:113`); the drawing is unchanged.
 */
/*
 * C-GTCC-SHELL-1ZU1 — Seitentitel = `h1` der Referenz
 * (GTCC_REFERENCE_2026-09-01.html:67): Sora 19 px/700 in `--tx`. Die Werte
 * kommen aus dem Token-Satz, damit jede Deck-View denselben Titel traegt;
 * vorher hat jede View ihre eigene Typo-Stufe gesetzt (15/19/20/24 px).
 */
const HEAD_TITLE_STYLE: CSSProperties = {
  margin: 0,
  fontFamily: "var(--gt-cmp-gtcc-view-title-family)",
  fontSize: "var(--gt-cmp-gtcc-view-title-size)",
  fontWeight: "var(--gt-cmp-gtcc-view-title-weight)",
  color: "var(--tx)",
};

const SUB_STYLE: CSSProperties = {
  margin: 0,
  fontSize: "var(--gt-sys-font-size-sm)",
  color: "var(--gt-sys-text-muted)",
};

/* Der Hinweis des Panelkopfs steht in der Referenz rechtsbuendig am Panelrand,
   nicht dicht neben dem Titel (Abnahme E-GTCC-ACCEPT1). `auto` als
   Anfangsabstand schiebt ihn in der Flex-Ueberschrift an die Endkante. */
const HEADING_EXTRA_STYLE: CSSProperties = {
  marginInlineStart: "auto",
  fontFamily: "var(--gt-sys-font-family-mono)",
  fontSize: "var(--gt-cmp-gtcc-mono-size)",
  fontWeight: "var(--gt-sys-font-weight-regular)",
  color: "var(--gt-sys-text-muted)",
};

/* Die Fussnote der Incident-Werkstatt steht in der Referenz mittig unter dem
   Panel, nicht linksbuendig unter dem Punkt (Abnahme E-GTCC-ACCEPT1). Die Mitte
   sitzt an der Aufrufstelle und nicht in `FleetNote`, weil dieselbe Primitive in
   `cockpit` und `dplane` linksbuendige Fussnoten traegt. */
const NOTE_CENTER_STYLE: CSSProperties = { textAlign: "center" };

/* `.grid-main` der Referenz (`cockpit-ui.css:189`): Spalten 1.9fr/1fr und
   `stretch`. Mit `start` blieb "Stumm-Schaltungen" auf seiner eigenen Hoehe
   stehen statt auf die Zeilenhoehe des Incident-Panels zu wachsen, und das
   2fr/1fr-Verhaeltnis machte die Spalte schmaler als in der Referenz
   (Abnahme E-GTCC-ACCEPT2). */
const GRID_MAIN_STYLE: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1.9fr) minmax(0, 1fr)",
  gap: "var(--gt-sys-space-3)",
  alignItems: "stretch",
};

/* A-GTCC-VIEW-ALIN-AUDIT1 — Tabellenschrift, Kopf-, Zellen- und Zeilenmasse
   sowie die Monozelle und die Trennlinie kommen aus den kanonischen
   `ControlPrimitives`. Lokal bleibt nur noch die Verbindung zweier
   kanonischer Bausteine und das, was die Referenz an dieser Zelle
   zusaetzlich schreibt (`nowrap`, `font-weight:600`). Die Trennlinie hat
   diese Sicht vorher selbst aufgebaut, die Monozelle mit
   `--gt-sys-font-size-xs` (12 px) statt den 11 px der Referenz. */
const HEAD_CELL_STYLE: CSSProperties = { ...TH_STYLE, ...CELL_RULE_STYLE };

const CELL_STYLE: CSSProperties = { ...TD_STYLE, ...CELL_RULE_STYLE };

/** `.mono` of the reference. */
const MONO_RULED_CELL_STYLE: CSSProperties = {
  ...MONO_CELL_STYLE,
  ...CELL_RULE_STYLE,
  whiteSpace: "nowrap",
};

/** `.host` of the reference — the operator-readable alarm headline. */
const HOST_CELL_STYLE: CSSProperties = {
  ...CELL_STYLE,
  fontWeight: "var(--gt-sys-font-weight-semibold)",
};

const ROW_TAIL_STYLE: CSSProperties = {
  marginInlineStart: "auto",
  fontFamily: "var(--gt-sys-font-family-mono)",
  fontSize: "var(--gt-cmp-gtcc-mono-size)",
  color: "var(--gt-sys-text-muted)",
};

const ACTION_CELL_STYLE: CSSProperties = {
  ...MONO_RULED_CELL_STYLE,
  display: "flex",
  gap: "var(--gt-sys-space-2)",
  alignItems: "center",
};

/* The reference writes the two verbs as mono bracket text (`[Ack] [Besitzer]`),
 * not as framed buttons. They stay real buttons — the cell carries actions, and
 * the design system's minimum target size is an accessibility decision, not a
 * look — but they lose the frame and the fill and take the mono face of the
 * surrounding cell. The remaining row height above the reference's 34 px comes
 * from that target floor and is listed, not removed. */
const ACTION_BUTTON_STYLE: CSSProperties = {
  fontFamily: "var(--gt-sys-font-family-mono)",
  fontSize: "var(--gt-cmp-gtcc-mono-size)",
  fontWeight: "var(--gt-sys-font-weight-regular)",
  paddingInline: "var(--gt-sys-space-1)",
  gap: 0,
};

/** `[…]` of the reference — decoration, so it stays out of the button's name. */
function BracketLabel(props: { children: ReactNode }): ReactNode {
  return (
    <>
      <span aria-hidden="true">[</span>
      {props.children}
      <span aria-hidden="true">]</span>
    </>
  );
}

/* ------------------------------------------------------------------ panel */

function Panel(props: {
  title: string;
  extra?: ReactNode;
  fresh?: ReactNode;
  testId: string;
  children: ReactNode;
}): ReactNode {
  return (
    <Card elevation={0}>
      <Stack gap={2}>
        {/* h3 like every other GTCC panel (`InfraTab.tsx`): the view's single
            level-1/2 heading is its `h2` title, pinned by `gtcc-a11y-smoke`. */}
        <h3 style={PANEL_HEADING_STYLE} data-testid={`${props.testId}-title`}>
          {props.title}
          {props.extra}
          {props.fresh}
        </h3>
        <div data-testid={props.testId}>{props.children}</div>
      </Stack>
    </Card>
  );
}

/**
 * One gate for the single endpoint of this view: a pending or failed read
 * never falls back to stale rows, it says what happened. `disabled` is the
 * honest answer while K-GTCC-API-SVC1 is unmerged.
 */
function AlinGate<T>(props: {
  query: UseQueryResult<AlertsResult<T>>;
  testId: string;
  render: (data: T) => ReactNode;
}): ReactNode {
  const { t } = useLocale();
  const { query } = props;

  if (query.data !== undefined && query.data.ok) return props.render(query.data.data);

  /*
   * `fetchGtccAlertsSummary` turns every failure into an `AlertsResult`, so a
   * rejected query is the one case it cannot describe: the request never got
   * far enough to answer. It is still a failure and is shown as one — treating
   * it as "still loading" would leave a spinner spinning forever.
   */
  const failure =
    query.data !== undefined
      ? query.data
      : query.isError
        ? ({ ok: false, kind: "transport_error", message: String(query.error) } as const)
        : null;

  if (failure === null) {
    return (
      <div data-testid={`${props.testId}-loading`}>
        {/* `LoadingState` carries role="status" + aria-live: a screen reader
            has to hear that the view is still measuring, not silence. */}
        <LoadingState label={t("admin.gtcc.alin.loading")} />
      </div>
    );
  }

  const body =
    failure.kind === "disabled"
      ? t("admin.gtcc.alin.failure.disabled")
      : failure.kind === "forbidden"
        ? t("admin.gtcc.alin.failure.forbidden")
        : failure.kind === "upstream"
          ? t("admin.gtcc.alin.failure.upstream", { status: failure.httpStatus })
          : t("admin.gtcc.alin.failure.transport");
  return (
    <div data-testid={`${props.testId}-failure`}>
      <ErrorState severity="warn" title={t("admin.gtcc.alin.failure.title")} body={body} />
    </div>
  );
}

/* ------------------------------------------------------------- ack dialog */

interface AckDraft {
  readonly fingerprint: string;
  readonly headline: string;
}

function AckDialog(props: {
  draft: AckDraft | null;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (reason: string, hours: number) => void;
}): ReactNode {
  const { t } = useLocale();
  const [reason, setReason] = useState("");
  const [hours, setHours] = useState(String(ACK_MAX_HOURS));

  const { draft } = props;
  if (draft === null) return null;

  const parsedHours = Number.parseInt(hours, 10);
  const hoursValid =
    Number.isInteger(parsedHours) && parsedHours >= 1 && parsedHours <= ACK_MAX_HOURS;
  const reasonValid = reason.trim().length > 0;

  return (
    <Modal
      open
      onClose={props.onClose}
      dismissible={!props.pending}
      title={t("admin.gtcc.alin.ack.dialog.title")}
      dataTestId="gtcc-alin-ack-dialog"
    >
      <Stack gap={3}>
        <p style={SUB_STYLE} data-testid="gtcc-alin-ack-dialog-alarm">
          {draft.headline}
        </p>
        <TextInput
          label={t("admin.gtcc.alin.ack.dialog.reason")}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          hint={t("admin.gtcc.alin.ack.dialog.reason_hint")}
          data-testid="gtcc-alin-ack-reason"
        />
        <TextInput
          label={t("admin.gtcc.alin.ack.dialog.hours")}
          type="number"
          min={1}
          max={ACK_MAX_HOURS}
          value={hours}
          onChange={(event) => setHours(event.target.value)}
          hint={t("admin.gtcc.alin.ack.dialog.hours_hint", { max: ACK_MAX_HOURS })}
          data-testid="gtcc-alin-ack-hours"
        />
        {props.error === null ? null : (
          <ErrorState
            severity="warn"
            title={t("admin.gtcc.alin.ack.dialog.failed")}
            body={props.error}
          />
        )}
        <div style={{ display: "flex", gap: "var(--gt-sys-space-2)" }}>
          <Button
            variant="primary"
            loading={props.pending}
            disabled={!reasonValid || !hoursValid || props.pending}
            onClick={() => props.onSubmit(reason.trim(), parsedHours)}
            data-testid="gtcc-alin-ack-submit"
          >
            {t("admin.gtcc.alin.ack.dialog.submit")}
          </Button>
          <Button
            variant="secondary"
            disabled={props.pending}
            onClick={props.onClose}
            data-testid="gtcc-alin-ack-cancel"
          >
            {t("admin.gtcc.alin.ack.dialog.cancel")}
          </Button>
        </div>
      </Stack>
    </Modal>
  );
}

/* ----------------------------------------------------------- owner dialog */

function OwnerDialog(props: {
  draft: AckDraft | null;
  current: string | null;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (owner: string | null) => void;
}): ReactNode {
  const { t } = useLocale();
  const [owner, setOwner] = useState(props.current ?? "");

  const { draft } = props;
  if (draft === null) return null;

  const trimmed = owner.trim();

  return (
    <Modal
      open
      onClose={props.onClose}
      dismissible={!props.pending}
      title={t("admin.gtcc.alin.owner.dialog.title")}
      dataTestId="gtcc-alin-owner-dialog"
    >
      <Stack gap={3}>
        <p style={SUB_STYLE} data-testid="gtcc-alin-owner-dialog-alarm">
          {draft.headline}
        </p>
        <TextInput
          label={t("admin.gtcc.alin.owner.dialog.owner")}
          value={owner}
          onChange={(event) => setOwner(event.target.value)}
          hint={t("admin.gtcc.alin.owner.dialog.owner_hint")}
          data-testid="gtcc-alin-owner-input"
        />
        {props.error === null ? null : (
          <ErrorState
            severity="warn"
            title={t("admin.gtcc.alin.owner.dialog.failed")}
            body={props.error}
          />
        )}
        <div style={{ display: "flex", gap: "var(--gt-sys-space-2)" }}>
          <Button
            variant="primary"
            loading={props.pending}
            disabled={props.pending}
            onClick={() => props.onSubmit(trimmed.length > 0 ? trimmed : null)}
            data-testid="gtcc-alin-owner-submit"
          >
            {t("admin.gtcc.alin.owner.dialog.submit")}
          </Button>
          <Button
            variant="secondary"
            disabled={props.pending}
            onClick={props.onClose}
            data-testid="gtcc-alin-owner-cancel"
          >
            {t("admin.gtcc.alin.owner.dialog.cancel")}
          </Button>
        </div>
      </Stack>
    </Modal>
  );
}

/* ------------------------------------------------------------ alarm table */

export function alertHeadline(row: GtccAlertRowDto): string {
  /* Owner copy when the rule carries one, otherwise the implementation
     alertname — never an invented sentence. */
  return row.ownerText !== null && row.ownerText.trim().length > 0 ? row.ownerText : row.alertname;
}

function AlarmTable(props: {
  rows: ReadonlyArray<GtccAlertRowDto>;
  onAck: (row: GtccAlertRowDto) => void;
  onOwner: (row: GtccAlertRowDto) => void;
}): ReactNode {
  const { t, locale } = useLocale();

  if (props.rows.length === 0) {
    return (
      <div data-testid="gtcc-alin-alarms-empty">
        <EmptyState
          title={t("admin.gtcc.alin.alarms.empty.title")}
          body={t("admin.gtcc.alin.alarms.empty.body")}
        />
      </div>
    );
  }

  return (
    <table style={TABLE_STYLE} data-testid="gtcc-alin-alarms-table">
      {/* The reference prints no caption and leaves the dot column headless.
          Both names are rendered and visually hidden instead of dropped: the
          repo's table inventory requires a named table and a named column
          header, and the drawing stays byte-identical. */}
      <caption style={GTCC_VISUALLY_HIDDEN}>{t("admin.gtcc.alin.alarms.title")}</caption>
      <thead>
        <tr>
          <th scope="col" style={HEAD_CELL_STYLE}>
            <span style={GTCC_VISUALLY_HIDDEN}>{t("admin.gtcc.alin.alarms.col.severity")}</span>
          </th>
          <th scope="col" style={HEAD_CELL_STYLE}>
            {t("admin.gtcc.alin.alarms.col.alarm")}
          </th>
          <th scope="col" style={HEAD_CELL_STYLE}>
            {t("admin.gtcc.alin.alarms.col.system")}
          </th>
          <th scope="col" style={HEAD_CELL_STYLE}>
            {t("admin.gtcc.alin.alarms.col.since")}
          </th>
          <th scope="col" style={HEAD_CELL_STYLE}>
            {t("admin.gtcc.alin.alarms.col.owner")}
          </th>
          <th scope="col" style={HEAD_CELL_STYLE}>
            {t("admin.gtcc.alin.alarms.col.action")}
          </th>
        </tr>
      </thead>
      <tbody>
        {props.rows.map((row) => (
          <tr key={row.fingerprint} data-testid={`gtcc-alin-alarm-${row.fingerprint}`}>
            <td style={CELL_STYLE}>
              <AlinDot
                tone={SEVERITY_TONE[row.severity]}
                testId={`gtcc-alin-alarm-dot-${row.fingerprint}`}
              />
              {/* The reference paints colour only. Colour alone is not a
                  reading, so the severity is spelled out for a screen reader
                  and hidden for the eye — the drawing is unchanged. */}
              <span style={GTCC_VISUALLY_HIDDEN}>
                {t(`admin.gtcc.alin.severity.${row.severity}`)}
              </span>
            </td>
            <td style={HOST_CELL_STYLE}>{alertHeadline(row)}</td>
            <td style={MONO_RULED_CELL_STYLE}>{row.system ?? <FleetDash />}</td>
            <td style={MONO_RULED_CELL_STYLE}>
              {row.ageSeconds === null ? <FleetDash /> : formatAge(row.ageSeconds, locale, t)}
            </td>
            <td style={MONO_RULED_CELL_STYLE}>
              {row.owner === null ? (
                <FleetDash testId={`gtcc-alin-alarm-owner-dash-${row.fingerprint}`} />
              ) : (
                row.owner
              )}
            </td>
            <td style={ACTION_CELL_STYLE}>
              {/* Die quittierte Zeile traegt in der Referenz nur "✓ Ack" und
                  keine zweite Marke; `[Besitzer]` steht dort ausschliesslich in
                  den offenen Zeilen (`GTCC_REFERENCE_2026-09-01.html:592-594`,
                  Abnahme E-GTCC-ACCEPT2). Der Besitzer einer quittierten Zeile
                  wird weiterhin in der Spalte BESITZER gelesen. */}
              {row.acknowledged ? (
                <span data-testid={`gtcc-alin-alarm-acked-${row.fingerprint}`}>
                  {t("admin.gtcc.alin.alarms.acked")}
                </span>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    style={ACTION_BUTTON_STYLE}
                    onClick={() => props.onAck(row)}
                    data-testid={`gtcc-alin-alarm-ack-${row.fingerprint}`}
                  >
                    <BracketLabel>{t("admin.gtcc.alin.alarms.ack")}</BracketLabel>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    style={ACTION_BUTTON_STYLE}
                    onClick={() => props.onOwner(row)}
                    data-testid={`gtcc-alin-alarm-assign-${row.fingerprint}`}
                  >
                    <BracketLabel>{t("admin.gtcc.alin.alarms.assign")}</BracketLabel>
                  </Button>
                </>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* --------------------------------------------------------------- panels */

function IncidentPanel(props: { data: GtccAlertsSummaryDto }): ReactNode {
  const { t } = useLocale();
  return (
    <Panel title={t("admin.gtcc.alin.incidents.title")} testId="gtcc-alin-incidents">
      <Stack gap={0}>
        {props.data.incidents.length === 0 ? (
          <div style={ROW_STYLE} data-testid="gtcc-alin-incidents-empty">
            <Dot tone="ok" />
            <span>{t("admin.gtcc.alin.incidents.none")}</span>
          </div>
        ) : (
          props.data.incidents.map((incident) => (
            <div
              key={incident.id}
              style={ROW_STYLE}
              data-testid={`gtcc-alin-incident-${incident.id}`}
            >
              <Dot tone={SEVERITY_TONE[incident.severity]} />
              <span>
                <b>{incident.name}</b>
                {` — ${incident.context}`}
              </span>
              {incident.runbookUrl === null ? (
                <span style={ROW_TAIL_STYLE}>{t("admin.gtcc.alin.incidents.no_runbook")}</span>
              ) : (
                <a
                  style={{ ...ROW_TAIL_STYLE, color: "var(--gt-sys-accent)" }}
                  href={incident.runbookUrl}
                  rel="noreferrer"
                  data-testid={`gtcc-alin-incident-open-${incident.id}`}
                >
                  {t("admin.gtcc.alin.incidents.open")}
                </a>
              )}
            </div>
          ))
        )}
        <div style={NOTE_CENTER_STYLE}>
          <FleetNote testId="gtcc-alin-incidents-note">
            {t("admin.gtcc.alin.incidents.note")}
          </FleetNote>
        </div>
      </Stack>
    </Panel>
  );
}

function SilencePanel(props: { data: GtccAlertsSummaryDto }): ReactNode {
  const { t, locale } = useLocale();
  return (
    <Panel title={t("admin.gtcc.alin.silences.title")} testId="gtcc-alin-silences">
      <Stack gap={0}>
        {props.data.silences.length === 0 ? (
          <div style={ROW_STYLE} data-testid="gtcc-alin-silences-none">
            <Dot tone="ok" />
            <span>{t("admin.gtcc.alin.silences.none")}</span>
            <span style={ROW_TAIL_STYLE}>
              {t("admin.gtcc.alin.silences.limit", { max: ACK_MAX_HOURS })}
            </span>
          </div>
        ) : (
          props.data.silences.map((silence) => (
            <div key={silence.id} style={ROW_STYLE} data-testid={`gtcc-alin-silence-${silence.id}`}>
              <Dot tone="warn" />
              <span>
                {silence.label}
                {` — ${silence.reason}`}
              </span>
              <span style={ROW_TAIL_STYLE}>
                {t("admin.gtcc.alin.silences.remaining", {
                  remaining: formatAge(silence.remainingSeconds, locale, t),
                })}
              </span>
            </div>
          ))
        )}
      </Stack>
    </Panel>
  );
}

/* ------------------------------------------------------------------ view */

export function AlinView(): ReactNode {
  const { t } = useLocale();
  const queryClient = useQueryClient();

  const [ackDraft, setAckDraft] = useState<AckDraft | null>(null);
  const [ownerDraft, setOwnerDraft] = useState<AckDraft | null>(null);
  const [ownerCurrent, setOwnerCurrent] = useState<string | null>(null);
  const [writeError, setWriteError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ALERTS_SUMMARY_KEY,
    queryFn: ({ signal }) => fetchGtccAlertsSummary(signal),
    ...ALIN_QUERY_OPTIONS,
  });

  const summary = query.data !== undefined && query.data.ok ? query.data.data : null;

  const failureMessage = useCallback(
    (result: AlertsResult<GtccAlertRowDto>): string | null => {
      if (result.ok) return null;
      if (result.kind === "disabled") return t("admin.gtcc.alin.failure.disabled");
      if (result.kind === "forbidden") return t("admin.gtcc.alin.failure.forbidden");
      if (result.kind === "upstream") {
        return t("admin.gtcc.alin.failure.upstream", { status: result.httpStatus });
      }
      return t("admin.gtcc.alin.failure.transport");
    },
    [t],
  );

  const ackMutation = useMutation({
    mutationFn: (input: { fingerprint: string; reason: string; hours: number }) =>
      ackGtccAlert(input.fingerprint, { reason: input.reason, hours: input.hours }),
    onSuccess: (result) => {
      const message = failureMessage(result);
      setWriteError(message);
      if (message !== null) return;
      setAckDraft(null);
      void queryClient.invalidateQueries({ queryKey: ALERTS_SUMMARY_KEY });
    },
  });

  const ownerMutation = useMutation({
    mutationFn: (input: { fingerprint: string; owner: string | null }) =>
      setGtccAlertOwner(input.fingerprint, { owner: input.owner }),
    onSuccess: (result) => {
      const message = failureMessage(result);
      setWriteError(message);
      if (message !== null) return;
      setOwnerDraft(null);
      void queryClient.invalidateQueries({ queryKey: ALERTS_SUMMARY_KEY });
    },
  });

  const openAck = useCallback((row: GtccAlertRowDto) => {
    setWriteError(null);
    setAckDraft({ fingerprint: row.fingerprint, headline: alertHeadline(row) });
  }, []);

  const openOwner = useCallback((row: GtccAlertRowDto) => {
    setWriteError(null);
    setOwnerCurrent(row.owner);
    setOwnerDraft({ fingerprint: row.fingerprint, headline: alertHeadline(row) });
  }, []);

  return (
    <Stack gap={3} data-testid="gtcc-alin">
      <Stack gap={1}>
        <div style={HEAD_ROW_STYLE}>
          <h2 style={HEAD_TITLE_STYLE} data-testid="gtcc-alin-title">
            {t("admin.gtcc.alin.title")}
          </h2>
          {summary === null ? null : (
            <AlinFreshBadge
              measuredAt={summary.measuredAt}
              stale={summary.status === "stale"}
              testId="gtcc-alin-fresh"
            />
          )}
        </div>
        <p style={SUB_STYLE} data-testid="gtcc-alin-sub">
          {t("admin.gtcc.alin.subtitle")}
        </p>
      </Stack>

      <Panel
        title={t("admin.gtcc.alin.alarms.title")}
        extra={
          <span style={HEADING_EXTRA_STYLE} data-testid="gtcc-alin-alarms-extra">
            {t("admin.gtcc.alin.alarms.extra")}
          </span>
        }
        testId="gtcc-alin-alarms"
      >
        <AlinGate
          query={query}
          testId="gtcc-alin-alarms"
          render={(data: GtccAlertsSummaryDto) => (
            <AlarmTable rows={data.alerts} onAck={openAck} onOwner={openOwner} />
          )}
        />
      </Panel>

      <div style={GRID_MAIN_STYLE}>
        <AlinGate
          query={query}
          testId="gtcc-alin-incidents-gate"
          render={(data: GtccAlertsSummaryDto) => <IncidentPanel data={data} />}
        />
        <AlinGate
          query={query}
          testId="gtcc-alin-silences-gate"
          render={(data: GtccAlertsSummaryDto) => <SilencePanel data={data} />}
        />
      </div>

      {/* Keyed by fingerprint: a new alarm opens a dialog with empty fields
          instead of inheriting the previous reason. */}
      <AckDialog
        key={ackDraft === null ? "ack-none" : `ack-${ackDraft.fingerprint}`}
        draft={ackDraft}
        pending={ackMutation.isPending}
        error={writeError}
        onClose={() => {
          setAckDraft(null);
          setWriteError(null);
        }}
        onSubmit={(reason, hours) => {
          if (ackDraft === null) return;
          ackMutation.mutate({ fingerprint: ackDraft.fingerprint, reason, hours });
        }}
      />
      <OwnerDialog
        key={ownerDraft === null ? "owner-none" : `owner-${ownerDraft.fingerprint}`}
        draft={ownerDraft}
        current={ownerCurrent}
        pending={ownerMutation.isPending}
        error={writeError}
        onClose={() => {
          setOwnerDraft(null);
          setWriteError(null);
        }}
        onSubmit={(owner) => {
          if (ownerDraft === null) return;
          ownerMutation.mutate({ fingerprint: ownerDraft.fingerprint, owner });
        }}
      />
    </Stack>
  );
}
